"use client";

import { useOptimistic, useTransition } from "react";
import { toast } from "sonner";
import { useAuthBasket } from "@/hooks/use-auth-basket";
import {
  type OptimisticBasketAction,
  optimisticBasketReducer,
} from "@/lib/basket-utils";
import {
  type BasketItem,
  useBasketStore,
  useOptimisticBasketActions,
} from "@/stores/basket-store";

/**
 * A hook that provides an optimistically updated basket state.
 * It uses React 19's `useOptimistic` hook to reflect UI changes instantly
 * while async server actions are pending.
 */
export function useOptimisticBasket() {
  const basketState = useBasketStore();
  const actions = useOptimisticBasketActions();
  const [isPending, startTransition] = useTransition();

  const { isBasketSynced } = useAuthBasket();

  const [optimisticState, addOptimistic] = useOptimistic(
    basketState,
    optimisticBasketReducer
  );

  const runOptimisticAction = (
    action: OptimisticBasketAction,
    asyncFn: () => Promise<void>
  ) => {
    startTransition(() => {
      addOptimistic(action);
      asyncFn().catch((_error) => {
        toast.error("Something went wrong.", {
          description: "Your basket could not be updated. Please try again.",
        });
      });
    });
  };

  return {
    // State
    basketCount: optimisticState.count,
    basketTotal: optimisticState.total,
    basketItems: optimisticState.items,
    isLoading: basketState.isLoading,
    isPending,
    isBasketSynced,

    // Actions with optimistic updates
    addItem: (item: Omit<BasketItem, "quantity"> & { quantity?: number }) => {
      runOptimisticAction({ type: "add", payload: item }, () =>
        actions.addItem(item)
      );
    },

    removeItem: (id: string, variantId?: string) => {
      runOptimisticAction({ type: "remove", payload: { id, variantId } }, () =>
        actions.removeItem(id, variantId)
      );
    },

    updateQuantity: (id: string, quantity: number, variantId?: string) => {
      runOptimisticAction(
        { type: "update", payload: { id, quantity, variantId } },
        () => actions.updateQuantity(id, quantity, variantId)
      );
    },

    clearBasket: () => {
      runOptimisticAction({ type: "clear" }, actions.clearBasket);
    },

    // Utilities
    getItemQuantity: basketState.getItemQuantity,
  };
}
